import { supabase } from '../lib/supabase'

/**
 * Every order read goes through the two display views of migration 0029,
 * public.orders_display and public.order_lines_display. Both are
 * security_invoker: orders_rep_read / order_lines_rep_read restrict every
 * row to what the caller is credited on via order_reps, so a plain select
 * here is already "my own orders". Nothing is filtered client-side.
 *
 * The display rules (cancelled lines, line value, order total, effective
 * ESL date, past promise) are computed in those views, once. This file and
 * the screens only word them; they never recompute a total or fall back to
 * required / expedite / P21 promise dates (none of which the views carry).
 */

/** A raw status string from P21 (Open / Allocated / Complete / Cancelled
 *  today). Badge styling maps known values; anything else renders neutral. */
export type RawStatus = string | null

export interface OrderSummary {
  orderNo: string
  customerName: string | null
  custPo: string | null
  jobName: string | null
  shipToCity: string | null
  shipToState: string | null
  orderDate: string | null
  status: RawStatus
  /** Sum of line values; negative for a return. Never null from the view. */
  orderTotal: number
  hasReturn: boolean
  hasPastPromise: boolean
  hasDatePending: boolean
  /** Earliest effective ESL date on an open line. */
  nextEslDate: string | null
  isShared: boolean
}

export type OrderSortKey = 'order_date' | 'order_total'

/** '12m' = order date in the last 12 months OR still open (JCP 2026-11-02:
 *  open orders are never hidden by the window). 'all' = everything we hold,
 *  which is since 2024. */
export type OrderWindow = '12m' | 'all'

export interface FetchOrdersParams {
  search: string
  sortKey: OrderSortKey
  sortAscending: boolean
  window: OrderWindow
  limit: number
}

export type OrdersResult =
  | { status: 'ok'; orders: OrderSummary[]; totalCount: number }
  | { status: 'error'; detail: string }

/**
 * Escapes a search term for safe use inside a quoted PostgREST `ilike`
 * value: `%`/`_` are LIKE wildcards (escaped so the term matches literally,
 * not as a pattern), `"` would end the quoted value early, and `\` escapes
 * both. Quoting the value itself (rather than leaving it bare) is what lets
 * the term contain a comma or parenthesis without corrupting `.or()`'s own
 * comma-separated filter-list syntax.
 */
export function likeTerm(raw: string): string {
  return raw.replace(/[\\%_"]/g, (c) => `\\${c}`)
}

/** Today in America/Denver as YYYY-MM-DD — the same "today" the views use. */
export function denverToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Denver',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/** The first order date inside the 12-month window. */
export function windowStart(today: string = denverToday()): string {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number]
  const start = new Date(Date.UTC(y - 1, m - 1, d))
  return start.toISOString().slice(0, 10)
}

const SUMMARY_COLUMNS =
  'order_no, customer_name, cust_po, job_name, ship_to_city, ship_to_state, order_date, status, ' +
  'order_total, has_return, has_past_promise, has_date_pending, next_esl_date, is_shared'

type SummaryRow = {
  order_no: string | null
  customer_name: string | null
  cust_po: string | null
  job_name: string | null
  ship_to_city: string | null
  ship_to_state: string | null
  order_date: string | null
  status: string | null
  order_total: number | null
  has_return: boolean | null
  has_past_promise: boolean | null
  has_date_pending: boolean | null
  next_esl_date: string | null
  is_shared: boolean | null
}

function toSummary(row: SummaryRow): OrderSummary {
  return {
    // order_no is the base table's primary key; codegen marks every view
    // column nullable because it does not trace NOT NULL through views.
    orderNo: row.order_no!,
    customerName: row.customer_name,
    custPo: row.cust_po,
    jobName: row.job_name,
    shipToCity: row.ship_to_city,
    shipToState: row.ship_to_state,
    orderDate: row.order_date,
    status: row.status,
    orderTotal: row.order_total ?? 0,
    hasReturn: row.has_return === true,
    hasPastPromise: row.has_past_promise === true,
    hasDatePending: row.has_date_pending === true,
    nextEslDate: row.next_esl_date,
    isShared: row.is_shared === true,
  }
}

export async function fetchOrders(params: FetchOrdersParams): Promise<OrdersResult> {
  let query = supabase.from('orders_display').select(SUMMARY_COLUMNS, { count: 'exact' })

  // Both filters are OR groups. Combined, they go into one and(...) tree,
  // so the request carries a single `or` parameter and nothing depends on
  // how PostgREST treats a repeated one.
  const windowGroup =
    params.window === '12m' ? `or(order_date.gte.${windowStart()},status.eq.Open)` : null
  const term = params.search.trim()
  const searchGroup = term
    ? (() => {
        const t = likeTerm(term)
        return `or(order_no.ilike."%${t}%",customer_name.ilike."%${t}%",cust_po.ilike."%${t}%")`
      })()
    : null
  if (windowGroup && searchGroup) query = query.or(`and(${windowGroup},${searchGroup})`)
  else if (windowGroup) query = query.or(windowGroup.slice(3, -1))
  else if (searchGroup) query = query.or(searchGroup.slice(3, -1))

  const { data, error, count } = await query
    .order(params.sortKey, { ascending: params.sortAscending, nullsFirst: false })
    // A stable tie-break, so paging never repeats or skips an order with an
    // equal date or total.
    .order('order_no', { ascending: false })
    .range(0, params.limit - 1)

  if (error) return { status: 'error', detail: error.message }

  const rows = data as unknown as SummaryRow[]
  return { status: 'ok', orders: rows.map(toSummary), totalCount: count ?? rows.length }
}

export interface OrderDetailHeader extends OrderSummary {
  shipToName: string | null
  requestedDate: string | null
}

/**
 * One order_lines_display row. cancelState / lineValue / isOpen /
 * isPastPromise / effectiveEslDate are the view's own values.
 */
export interface OrderLine {
  orderNo: string
  lineNo: number
  itemId: string | null
  itemDescription: string | null
  qtyOrdered: number | null
  qtyShipped: number | null
  salesPrice: number | null
  lineStatus: RawStatus
  cancelState: 'cancelled' | 'balance_cancelled' | 'normal'
  lineValue: number
  isReturn: boolean
  eslPromiseDate: string | null
  eslUpdatedPromiseDate: string | null
  effectiveEslDate: string | null
  isOpen: boolean
  isPastPromise: boolean
}

export const LINE_COLUMNS =
  'order_no, line_no, item_id, item_description, qty_ordered, qty_shipped, sales_price, line_status, ' +
  'cancel_state, line_value, is_return, esl_promise_date, esl_updated_promise_date, effective_esl_date, ' +
  'is_open, is_past_promise'

type LineRow = {
  order_no: string | null
  line_no: number | null
  item_id: string | null
  item_description: string | null
  qty_ordered: number | null
  qty_shipped: number | null
  sales_price: number | null
  line_status: string | null
  cancel_state: string | null
  line_value: number | null
  is_return: boolean | null
  esl_promise_date: string | null
  esl_updated_promise_date: string | null
  effective_esl_date: string | null
  is_open: boolean | null
  is_past_promise: boolean | null
}

export function toLine(row: LineRow): OrderLine {
  const cs = row.cancel_state
  return {
    orderNo: row.order_no!,
    lineNo: row.line_no!,
    itemId: row.item_id,
    itemDescription: row.item_description,
    qtyOrdered: row.qty_ordered,
    qtyShipped: row.qty_shipped,
    salesPrice: row.sales_price,
    lineStatus: row.line_status,
    cancelState: cs === 'cancelled' || cs === 'balance_cancelled' ? cs : 'normal',
    lineValue: row.line_value ?? 0,
    isReturn: row.is_return === true,
    eslPromiseDate: row.esl_promise_date,
    eslUpdatedPromiseDate: row.esl_updated_promise_date,
    effectiveEslDate: row.effective_esl_date,
    isOpen: row.is_open === true,
    isPastPromise: row.is_past_promise === true,
  }
}

export interface OrderInvoice {
  invoiceNo: string
  invoiceDate: string | null
  shipDate: string | null
  carrier: string | null
  invoiceTotal: number | null
}

export type OrderDetailResult =
  | { status: 'found'; order: OrderDetailHeader; lines: OrderLine[]; invoices: OrderInvoice[] }
  | { status: 'not_found' }
  | { status: 'error'; detail: string }

export async function fetchOrderDetail(orderNo: string): Promise<OrderDetailResult> {
  const [orderResult, linesResult, invoicesResult] = await Promise.all([
    supabase
      .from('orders_display')
      .select(`${SUMMARY_COLUMNS}, ship_to_name, requested_date`)
      .eq('order_no', orderNo)
      .maybeSingle(),
    supabase
      .from('order_lines_display')
      .select(LINE_COLUMNS)
      .eq('order_no', orderNo)
      // Line number, not date — reps, CS and P21 all address lines by this
      // number, and re-sorting would break correspondence with the order
      // document with no warning.
      .order('line_no', { ascending: true }),
    // invoices_rep_read (0036): an invoice is visible when the caller is
    // credited on it (invoice_reps) or on its order (order_reps). Who else
    // it is credited to (invoice_reps) stays hidden.
    supabase
      .from('invoices')
      .select('invoice_no, invoice_date, ship_date, carrier, invoice_total')
      .eq('order_no', orderNo)
      .order('invoice_date', { ascending: true, nullsFirst: false })
      .order('invoice_no', { ascending: true }),
  ])

  if (orderResult.error) return { status: 'error', detail: orderResult.error.message }
  if (linesResult.error) return { status: 'error', detail: linesResult.error.message }
  if (invoicesResult.error) return { status: 'error', detail: invoicesResult.error.message }
  if (!orderResult.data) return { status: 'not_found' }

  const o = orderResult.data as unknown as SummaryRow & {
    ship_to_name: string | null
    requested_date: string | null
  }
  return {
    status: 'found',
    order: { ...toSummary(o), shipToName: o.ship_to_name, requestedDate: o.requested_date },
    lines: (linesResult.data as unknown as LineRow[]).map(toLine),
    invoices: invoicesResult.data.map((row) => ({
      invoiceNo: row.invoice_no,
      invoiceDate: row.invoice_date,
      shipDate: row.ship_date,
      carrier: row.carrier,
      invoiceTotal: row.invoice_total,
    })),
  }
}
